import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import AutoFitTitle from "../components/AutoFitTitle";
import { getPost, resolveAsset, formatApiErrorDetail } from "../lib/api";
import { api } from "../lib/api";
import { usePostTypes } from "../lib/postTypes";
import { POST_HTML_PURIFY_CONFIG, sanitizeIframeHook, sanitizeAnchorAttrHook, flagSmallTextParagraphs } from "../lib/sanitize";
import RichBodyWithCarousels from "../components/RichBodyWithCarousels";
import CommentsSection from "../components/CommentsSection";
import { ShareButton } from "../components/ShareButton";
import PostHelmet from "../components/PostHelmet";
import RightColumnWidgets from "../components/RightColumnWidgets";
import PageTilesGrid from "../components/PageTilesGrid";

// Register the iframe allowlist hook once at module load.
DOMPurify.addHook("uponSanitizeElement", sanitizeIframeHook);
// Preserve admin-chosen `target` + `rel` on <a> tags (DOMPurify strips
// them by default).
DOMPurify.addHook("uponSanitizeAttribute", sanitizeAnchorAttrHook);

// A post body is "rich" (TipTap HTML) if it starts with any well-formed
// HTML opening tag. Pre-rich-editor posts are plain text and render as
// before with preserved whitespace. The previous version only accepted
// a hardcoded whitelist (`p|h[1-6]|ul|ol|blockquote|hr|img|figure|div|
// iframe`) which broke posts whose first node is an <a> wrapping an
// image (from the image-hyperlink feature) — the renderer fell back to
// the plain-text branch and dumped the raw HTML as text. Match any
// leading `<letter…>` so common TipTap outputs (`<a>`, `<span>`,
// `<section>`, `<pre>`…) all correctly render as HTML. The `[a-z]`
// prefix keeps text emoticons like `<3` out of the rich branch.
const isRichHtml = (body) =>
  typeof body === "string" && /^\s*<[a-z][a-z0-9]*(\s|>|\/)/i.test(body);

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [neighbours, setNeighbours] = useState({ newer: null, older: null });
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [rightWidgets, setRightWidgets] = useState([]);

  // Fetch the homepage right-column widgets once. We render them twice
  // below: desktop sidebar + a mobile-only block beneath the comments
  // section. Passing `widgets` as a prop avoids double-fetching.
  useEffect(() => {
    api
      .get("/settings/homepage-right-column")
      .then((r) => {
        const list = Array.isArray(r.data?.widgets) ? r.data.widgets : [];
        // Skip widgets the admin has marked hidden.
        setRightWidgets(list.filter((w) => w && w.enabled !== false));
      })
      .catch(() => setRightWidgets([]));
  }, []);
  const { label: postTypeLabel } = usePostTypes();

  const isRich = post?.body && isRichHtml(post.body);
  const sanitizeHtml = useMemo(
    () => (raw) => flagSmallTextParagraphs(DOMPurify.sanitize(raw, POST_HTML_PURIFY_CONFIG)),
    [],
  );

  useEffect(() => {
    let alive = true;
    getPost(id)
      .then((p) => {
        if (!alive) return;
        setPost(p);
        // Canonicalize old UUID-based links to the SEO-friendly slug URL
        // (e.g. someone's bookmarked /post/<uuid> from before slugs
        // existed) — swap the address bar without a full reload.
        if (p.slug && p.slug !== id) {
          navigate(`/post/${p.slug}`, { replace: true });
        }
      })
      .catch((e) => {
        if (!alive) return;
        // Only a genuine 404 from our own API means the post really
        // doesn't exist/isn't published — any other failure (network
        // error, timeout, blocked request, 5xx) is a load failure, NOT
        // proof the post is missing, so it must not show the same
        // "Post not found" message (that's what let a client-side
        // fetch failure masquerade as a missing article — see the
        // server-rendered `#__ssr_post_fallback` fallback below for the
        // primary fix; this is the secondary guard for the message
        // itself).
        if (e.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  // Once the real post has loaded client-side, remove the server-
  // rendered fallback article (see `_render_post_ssr_fallback` in
  // server.py) — it's no longer needed since the full interactive
  // article below now covers the same content. Left untouched on
  // failure so it stays as the page's real, visible content.
  useLayoutEffect(() => {
    if (!post) return;
    const fallback = document.getElementById("__ssr_post_fallback");
    if (fallback) fallback.remove();
  }, [post]);

  // Fetch newer/older siblings for the prev/next arrows. Runs after the
  // post itself loads so we don't waste a request on a 404.
  useEffect(() => {
    if (!post?.id) return undefined;
    let alive = true;
    api
      .get(`/posts/${post.id}/neighbours`)
      .then((r) => { if (alive) setNeighbours(r.data || { newer: null, older: null }); })
      .catch(() => { if (alive) setNeighbours({ newer: null, older: null }); });
    return () => { alive = false; };
  }, [post?.id]);

  // "You may also like" — the 3 most recent other posts.
  useEffect(() => {
    if (!post?.id) return undefined;
    let alive = true;
    api
      .get(`/posts/${post.id}/related`, { params: { limit: 3 } })
      .then((r) => { if (alive) setRelatedPosts(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setRelatedPosts([]); });
    return () => { alive = false; };
  }, [post?.id]);

  // Mobile/tablet swipe navigation between posts. Mirrors the Older/Newer
  // cards at the foot of the article — swipe right pulls the older post
  // in from the left, swipe left reveals the newer one. Ignores swipes
  // that start inside iframes/embeds, a horizontally-scrollable table
  // (`.tableWrapper` — otherwise a fast swipe meant to scroll a wide
  // table sideways was being read as "jump to next post" instead), or
  // any element opting out via `[data-no-swipe-nav]`.
  useEffect(() => {
    const { older, newer } = neighbours;
    if (!older && !newer) return undefined;
    const linkTo = (p) => `/post/${p.slug || p.id}`;

    let startX = 0;
    let startY = 0;
    let startedAt = 0;
    let skip = false;

    const onStart = (e) => {
      if (e.touches.length !== 1) { skip = true; return; }
      const t = e.touches[0];
      const target = e.target;
      skip = !!(
        target &&
        target.closest &&
        target.closest("iframe, video, [data-no-swipe-nav], .swiper, .lightbox, .tableWrapper")
      );
      startX = t.clientX;
      startY = t.clientY;
      startedAt = Date.now();
    };

    const onEnd = (e) => {
      if (skip) { skip = false; return; }
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const dt = Date.now() - startedAt;
      if (dt > 700) return;                       // long press, not a swipe
      if (Math.abs(dx) < 80) return;              // too short
      if (Math.abs(dy) > 60) return;              // too much vertical drift
      if (Math.abs(dx) < Math.abs(dy) * 2) return; // not horizontal enough
      if (dx < 0 && older) {
        navigate(linkTo(older));
      } else if (dx > 0 && newer) {
        navigate(linkTo(newer));
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [neighbours, navigate]);

  return (
    <div
      className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]"
      data-testid="page-post-detail"
    >
      {/* Per-post OG / Twitter meta tags — client-side head manager, only
          invoked once the real post has actually loaded. Previously this
          rendered unconditionally with `post?.title` etc., which meant
          while `post` was null (still loading, or the fetch failed) it
          actively overwrote whatever meta was already in the document
          head with generic site-wide defaults — destroying any chance of
          showing article-specific meta for a slow/failed load. Now it
          simply doesn't run until there's real data to show, leaving
          whatever's already there untouched in the meantime. */}
      {post ? (
        <PostHelmet
          title={post.title}
          description={post.custom_intro || post.body}
          image={post.cover_image || (post.id ? `/api/og/post/${post.id}.png` : undefined)}
          url={typeof window !== "undefined" ? `${window.location.origin}/post/${post.slug}` : undefined}
          type="article"
        />
      ) : null}
      <Header />

      <main className="mx-auto max-w-7xl px-5 pt-6 pb-12 sm:px-8 sm:pt-8 sm:pb-16">
        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-[var(--brand-body)]">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : notFound ? (
          <div className="mt-10 rounded-md border border-dashed border-black/15 p-10 text-center" data-testid="post-not-found">
            <p className="font-display text-lg font-bold">Post not found.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              The link may be broken or the post was unpublished.
            </p>
          </div>
        ) : error || !post ? (
          <div className="mt-10 rounded-md border border-dashed border-black/15 p-10 text-center" data-testid="post-load-error">
            <p className="font-display text-lg font-bold">Couldn't load this article.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              {error || "Something went wrong. Please try refreshing the page."}
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-12 gap-6">
            <article
              className="col-span-12 rounded-md border border-black/10 bg-white p-6 sm:p-10 lg:col-span-8"
              data-testid="post-article"
            >
            {post.cover_image && post.cover_layout === "side" ? (
              <div className="flex flex-col sm:flex-row gap-5 items-start justify-between mt-3 mb-4" data-testid="post-header-side">
                <div className="flex-1 min-w-0">
                  <p className="label-kicker text-[#6CABDD]">
                    {postTypeLabel(post.post_type)}
                  </p>
                  <AutoFitTitle
                    className="mt-2 font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl text-[var(--brand-ink)]"
                    data-testid="post-title"
                  >
                    {post.title}
                  </AutoFitTitle>
                </div>
                <div
                  className="w-full sm:w-[180px] shrink-0 overflow-hidden rounded-md border border-black/10 bg-[#F8FAFC]"
                  style={{ aspectRatio: post.cover_aspect ?? 4 / 3 }}
                  data-testid="post-cover-wrap-side"
                >
                  <img
                    src={resolveAsset(post.cover_image)}
                    alt=""
                    className="h-full w-full object-cover"
                    style={{
                      objectPosition: `${post.cover_focal_x ?? 50}% ${post.cover_focal_y ?? 50}%`,
                    }}
                    data-testid="post-cover"
                  />
                </div>
              </div>
            ) : (
              <>
                <p className="label-kicker text-[#6CABDD]">
                  {postTypeLabel(post.post_type)}
                </p>
                <AutoFitTitle
                  className="mt-3 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl"
                  data-testid="post-title"
                >
                  {post.title}
                </AutoFitTitle>
              </>
            )}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <p
                  className="label-kicker text-[var(--brand-body)]"
                  data-testid="post-byline"
                >
                  {new Date(post.display_at || post.created_at).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                    timeZone: "Europe/London",
                    timeZoneName: "short",
                  })}
                </p>
              </div>
              <ShareButton
                url={
                  typeof window !== "undefined"
                    ? post.short_code
                      ? `${window.location.origin}/s/${post.short_code}`
                      : window.location.href
                    : ""
                }
                title={post.title}
              />
            </div>
            {post.cover_image && post.cover_layout !== "side" && post.cover_layout !== "hidden" ? (
              <div
                className="mt-8 w-full overflow-hidden rounded-md border border-black/10 bg-[#F8FAFC]"
                style={{ aspectRatio: post.cover_aspect ?? 16 / 9 }}
                data-testid="post-cover-wrap"
              >
                <img
                  src={resolveAsset(post.cover_image)}
                  alt=""
                  className="h-full w-full object-cover"
                  style={{
                    objectPosition: `${post.cover_focal_x ?? 50}% ${post.cover_focal_y ?? 50}%`,
                  }}
                  data-testid="post-cover"
                />
              </div>
            ) : null}
            {isRich ? (
              <RichBodyWithCarousels
                html={post.body}
                sanitize={sanitizeHtml}
                className="tiptap-prose prose prose-slate mt-8 max-w-none text-[var(--brand-ink)]"
                testId="post-body"
              />
            ) : (
              <div
                className="prose-post mt-8 whitespace-pre-wrap font-display text-lg leading-relaxed text-[var(--brand-ink)]"
                data-testid="post-body"
              >
                {post.body}
              </div>
            )}
            {Array.isArray(post.images) && post.images.length > 0 ? (
              <div
                className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2"
                data-testid="post-gallery"
              >
                {post.images.map((url) => (
                  <div
                    key={url}
                    className="w-full overflow-hidden rounded-md border border-black/10 bg-[#F8FAFC]"
                    style={{ aspectRatio: "4 / 3" }}
                  >
                    <img
                      src={resolveAsset(url)}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : null}
            {relatedPosts.length > 0 ? <RelatedPosts posts={relatedPosts} /> : null}
          </article>
            <RightColumnWidgets
              widgets={rightWidgets}
              className="order-3 hidden flex-col gap-4 lg:order-2 lg:col-span-4 lg:flex"
              testId="post-right-widgets-desktop"
            />
          </div>
        )}
        {/* Explore tiles — same admin-controlled grid that appears
            elsewhere on the site, anchored to the "home" page tiles key
            so they mirror what visitors see on the homepage. */}
        {post ? (
          <div className="mt-10" data-testid="post-explore-tiles">
            <PageTilesGrid pageKey="home" kicker="Explore" heading="More from LAST" />
          </div>
        ) : null}
        {post ? <PostNav newer={neighbours.newer} older={neighbours.older} /> : null}
        {post ? <CommentsSection post={post} /> : null}
        {/* Mobile-only widgets block — sits below the comments section
            per the user's preference so blog reading flows naturally on
            phones without the sidebar interrupting. */}
        {post ? (
          <RightColumnWidgets
            widgets={rightWidgets}
            className="mt-10 flex flex-col gap-4 lg:hidden"
            testId="post-right-widgets-mobile"
          />
        ) : null}
      </main>

      <Footer />
    </div>
  );
}

// Bottom-of-post navigation. Hidden when both sides are null (single post
// on the site). Each arrow shows the sibling's title for context.
function PostNav({ newer, older }) {
  if (!newer && !older) return null;
  const linkTo = (p) => `/post/${p.slug || p.id}`;
  return (
    <nav
      className="mt-10 border-t border-black/10 pt-6"
      data-testid="post-nav"
      aria-label="Other posts"
    >
      <p
        className="mb-3 text-center text-xs text-[var(--brand-body)] sm:hidden"
        data-testid="post-nav-swipe-hint"
      >
        Tip: swipe left/right to jump between posts.
      </p>
      <div className="grid grid-cols-2 gap-3">
      <div>
        {newer ? (
          <Link
            to={linkTo(newer)}
            data-testid="post-nav-newer"
            className="group flex h-full flex-col gap-1 rounded-md border border-black/10 bg-white p-4 transition-colors hover:border-[#0A0F17]"
          >
            <span className="inline-flex items-center gap-1 label-kicker text-[var(--brand-body)]">
              <ArrowLeft size={11} strokeWidth={2.5} /> Previous
            </span>
            <span className="line-clamp-2 font-display text-sm font-bold leading-tight transition-colors group-hover:text-[#5A9AC9]">
              {newer.title}
            </span>
          </Link>
        ) : null}
      </div>
      <div>
        {older ? (
          <Link
            to={linkTo(older)}
            data-testid="post-nav-older"
            className="group flex h-full flex-col items-end gap-1 rounded-md border border-black/10 bg-white p-4 text-right transition-colors hover:border-[#0A0F17]"
          >
            <span className="inline-flex items-center gap-1 label-kicker text-[var(--brand-body)]">
              Next <ArrowRight size={11} strokeWidth={2.5} />
            </span>
            <span className="line-clamp-2 font-display text-sm font-bold leading-tight transition-colors group-hover:text-[#5A9AC9]">
              {older.title}
            </span>
          </Link>
        ) : null}
      </div>
      </div>
    </nav>
  );
}

// "You may also like" — the 3 most recent other posts, shown as plain
// text headline links (no thumbnails/cards) so the section reads as a
// continuation of the article rather than a separate promo block.
function RelatedPosts({ posts }) {
  return (
    <section
      className="mt-10 border-t border-black/10 pt-6"
      data-testid="post-related"
      aria-label="You may also like"
    >
      <p className="label-kicker text-[#6CABDD]">You may also like</p>
      <ul className="mt-3 space-y-2">
        {posts.map((p) => (
          <li key={p.id}>
            <Link
              to={`/post/${p.slug || p.id}`}
              data-testid={`post-related-item-${p.id}`}
              className="font-display text-base font-bold leading-snug text-[var(--brand-ink)] underline decoration-transparent transition-colors hover:text-[#5A9AC9] hover:decoration-[#5A9AC9]"
            >
              {p.title}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
